import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import { PaymentCondition } from '../../../app/models/PaymentCondition';
import useQuoteService from '../../../app/services/hooks/useQuoteService';
import { Currency } from '../../../app/models/Currency';
import { QuoteDataForm, QuoteOfferTypeDataForm, QuoteReferenceDataForm } from '../../../app/models/Quote';

import LazyLoading from '../../../components/LazyLoading';
import DefaultCard from '../../../components/default/DefaultCard';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

import { toast } from 'react-toastify';
import { QuoteRouteSegment, QuoteRouteSegmentFilters } from '../../../app/models/QuoteRouteSegment';
import useQuoteRouteSegmentService from '../../../app/services/hooks/useQuoteRouteSegmentService';
import useClientService from '../../../app/services/hooks/useClientService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { Sector } from '../../../app/models/Sector';
import { QuoteMaxPaymentsEnum, SectorBusinessRulesEnum } from '../../../app/shared/enums';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import QuoteEditContainer from '../components/containers/QuoteEditContainer';
import WareHouseQuoteEditContainer from '../components/containers/WareHouseQuoteEditContainer';
import { QuoteInvoicingDataForm } from '../../../app/models/QuoteInvoicing';
import useQuoteInvoicingService from '../../../app/services/hooks/useQuoteInvoicingService';
import { ExchangeRateDate } from '../../../app/models/ExchangeRateDate';
import { ExchangeRateDateSource } from '../../../app/models/ExchangeRateDateSource';
import { ClientContact } from '../../../app/models/ClientContact';
import { Unit } from '../../../app/models/Unit';
import useUnitService from '../../../app/services/hooks/useUnitService';

type RouterParams = {
    id: string;
};

const QuoteEdit = () => {

    const { navigationPage } = useNavigationPage();
    const { id } = useParams<RouterParams>();

    const [verifyingQuote, setVerifyingQuote] = useState<boolean>(true);

    const { editQuote, fetchingEditQuote, updateQuote } = useQuoteService();
    const { getQuoteRouteSegments } = useQuoteRouteSegmentService();
    const {
        editQuoteInvoicing,
        updateQuoteInvoicing,
        fetchingQuoteInvoicingByQuote,
        fetchingUpdateQuoteInvoicing
    } = useQuoteInvoicingService();
    const { getUnits } = useUnitService();

    const {
        getClientsBySociety,
        fetchingGetClientBySociety
    } = useClientService();
    const [quoteForm, setQuoteForm] = useState<QuoteDataForm>({
        contact_email: '',
        expired_at: '',
        expired_in_days: 0,
        client_id: 0,
        client_contact_id: 0,
        payment_method_id: 0,
        payment_condition_id: 0,
        currency_id: 0,
        society_id: 0,
        sector_id: 0
    });

    const [societies, setSocieties] = useState<Society[]>([]);
    const [, setEditSocieties] = useState<Society[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [, setEditClients] = useState<Client[]>([]);
    const [paymentConditions, setPaymentsConditions] = useState<PaymentCondition[]>([]);
    const [references, setReferences] = useState<QuoteReferenceDataForm[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentCondition[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [offerTypes, setOfferTypes] = useState<QuoteOfferTypeDataForm[]>([]);
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [exchangeRateDate, setExchangeRateDate] = useState<ExchangeRateDate[]>([]);
    const [exchangeRateDateSource, setExchangeRateDateSource] = useState<ExchangeRateDateSource[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [clientContacts, setClientContatcs] = useState<ClientContact[]>([]);

    const { showLoading, hideLoading } = useContext(AppContext);

    const [quoteRouteSegments, setQuoteRouteSegments] = useState<QuoteRouteSegment[]>([]);
    const [currencyId, setCurrencyId] = useState(0);
    const [sectorId, setSectorId] = useState(0);

    const sectorBusinessRulesEnum = quoteForm.sector_business_rules_enum;   

    const [quoteCheckDocument, setQuoteCheckDocument] = useState<boolean>(false);
    const [disableUpdate, setDisableUpdate] = useState<boolean>(false);
    const clientIdSelected = useRef<number | undefined>(0);
    const societyIdSelected = useRef<number | undefined>(0);
    const quoteInitial = useRef<QuoteDataForm>({
        contact_email: '',
        expired_at: '',
        expired_in_days: 0,
        client_id: 0,
        client_contact_id: 0,
        payment_method_id: 0,
        payment_condition_id: 0,
        currency_id: 0,
        society_id: 0,
        sector_id: 0
    });
    const [quoteInvoicingForm, setQuoteInvoicingForm] = useState<QuoteInvoicingDataForm>({
        payment_method_id: 0,
        payment_condition_id: 0,
        currency_id: 0,
        quote_id: 0,
        expired_at: '',
        expired_in_days: 0,
        use_standard_weight: false,
        standard_weight_value: null,
        standard_weight_unit_id: null,
        standard_weight_lower_limit: null,
        standard_weight_lower_limit_unit_id: null,
        max_payments: QuoteMaxPaymentsEnum.UN_PAGO
    });

    const loadUnits = () => {
        getUnits(
            {
                page: 1,
                per_page: 100,
                sort: 'name',
                order: 'asc',
                is_active: true
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setUnits(response.data.units ?? []);
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                }
            }
        );
    };


    const clientsInitial = useRef<Client[]>([]);

    const routeSegmentsFilterRef = useRef<QuoteRouteSegmentFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        edit(parseInt(id));
        editInvoicing(parseInt(id));
        // reloadQuoteInvoicing(Number(id));

    }, [id]);

    const [errorFields, setErrorFields] = useState<Record<string, unknown> | undefined>();
    const [condition, setCondition] = useState<boolean>(false);

    const edit = (id: number) => {
        editQuote(id, {
            onSuccess: (response: ServiceResponse) => {
                const societies = response.data.societies.map((society: Society) => {
                    if(society.id === response.data.quote.society_id){
                        const _society = {
                            ...society,
                            name: response.data.quote.society_name
                        }
                        return _society;
                    }
                    return society;
                });
                setSocieties(societies);
                setEditSocieties(response.data.societies);

                const clients = response.data.clients.map((client: Client) => {
                    if(client.id === response.data.quote.client_id){
                        const _client = {
                            ...client,
                            name: response.data.quote.client_name
                        }
                        return _client;
                    }
                    return client;
                }
                );

                setClients(clients);
                setEditClients(response.data.clients);

                setClientContatcs(response.data.client_contacts)
                setOfferTypes(response.data.offer_types);
                setSectors(response.data.sectors)
                setExchangeRateDate(response.data.exchange_rate_date)
                setExchangeRateDateSource(response.data.exchange_rate_date_source)
                setReferences(response.data.references);
                if (response.data.quote.days_count) {
                    setReferences(response.data.references);
                    setQuoteForm({ ...quoteForm, ['reference']: '', ['days_count']: 0 });
                    setCondition(true);
                } else {
                    setPaymentsConditions(response.data.payment_conditions);
                    setCondition(false);
                }
                
                setQuoteForm(response.data.quote);
                quoteInitial.current = response.data.quote;
                clientsInitial.current = response.data.clients;
                societyIdSelected.current = response.data.quote.society_id;
                clientIdSelected.current = response.data.quote.client_id;
                setCurrencyId(response.data.quote.currency_id);
                setSectorId(response.data.quote.sector_id)
                setQuoteCheckDocument(response.data.quote.is_route_segment_document);
                
                if (!response.data.can_update) {
                    setDisableUpdate(true);
                }
                setVerifyingQuote(false);

            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                navigationPage('/quotes');
                
            }
        });
    };

    useEffect(() => {
        
        if (quoteForm.society_id == undefined) {
            societyIdSelected.current = undefined;
        }
        
        if (quoteForm.society_id && quoteForm.society_id != societyIdSelected.current) {
            
            if((quoteInitial.current && 
                quoteInitial.current.society_id != quoteForm.society_id) ||
                (!quoteInitial.current)
            ){
                
                getClientsBySociety(Number(quoteForm.society_id), {
                    onSuccess: (response: ServiceResponse) => {
                        societyIdSelected.current = Number(quoteForm.society_id) ?? 0;
                        
                        setClients(response.data.clients);
                        setQuoteForm(
                            {
                                ...quoteForm,
                                client_id: 0
                            }
                        )
                    },
                    onError: () => {
                        setQuoteForm({
                            ...quoteForm,
                            society_id: Number(societyIdSelected.current)
                        });
                    }
                })
            }
            else if(
                (quoteInitial.current && 
                    quoteInitial.current.society_id == quoteForm.society_id)
                ){
                    societyIdSelected.current = Number(quoteForm.society_id) ?? 0;
                    setClients(clientsInitial.current);
                    setQuoteForm(
                        {
                            ...quoteForm,
                            client_id: quoteInitial.current.client_id
                        }
                    )
                    
                }
                
                
            }
            
        }, [quoteForm.society_id]);
        

    const editInvoicing = (id: number) => {
    editQuoteInvoicing(id, {
        onSuccess: (response: ServiceResponse) => {
        const data = response?.data;
        if (data) {
            setPaymentsConditions(data.payment_conditions);
            setPaymentMethods(data.payment_methods);
            setCurrencies(data.currencies);
            if (Array.isArray(data.units) && data.units.length > 0) {
                setUnits(data.units);
            } else {
                loadUnits();
            }
            setQuoteInvoicingForm(prev => ({ ...prev, ...data.quote_invoicing }));
        }
        }
        ,
        onError: () => {
            loadUnits();
        }
    });
    };


    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando centro de costo...');

        if (!quoteForm?.id) {
            toast.error('No es posible actualizar la cotización sin un identificador.');
            if (hideLoading) hideLoading();
            return;
        }

        updateQuote(quoteForm.id, quoteForm, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
    
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
    
                toast.success(response.message, { autoClose: 2500 });
                setErrorFields(undefined);
                setQuoteForm({ ...quoteForm, ...response.data.quote });
                edit(parseInt(id));
           
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

    
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });

                setQuoteForm({
                    ...quoteForm,
                    sector_id: quoteInitial.current.sector_id
                });
                toast.error(response.message);
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();

                setErrorFields(errorFields.data);
            }
        });
    };

    const reloadQuoteRouteSegmentTable = () => {
        getQuoteRouteSegments(parseInt(id), routeSegmentsFilterRef.current, {
            onSuccess: (response: ServiceResponse) => {
                setQuoteRouteSegments(response.data.quote_route_segments);
            }
        });
    };

    



    // useEffect(() => {
    //     if (id) {
    //         getQuoteInvoicingByQuote(Number(id), {
    //             onSuccess: (response: ServiceResponse) => {
    //                 setQuoteInvoicingForm(response.data.quote_invoicing);
    //             },
    //             onError: (response: ServiceResponse) => {
    //                 toast.error('Error al cargar información de facturación.', { autoClose: 2000 });
    //             }
    //         });
    //     }
    // }, [id]);

    const updateInvoicing = () => {
        if (quoteForm != null && quoteForm.id != null) {
            if (showLoading) showLoading('loading', 'Actualizando facturación de cotización...');

            // ✅ Copia y limpia los datos según el tipo de fecha
            const cleaned = { ...quoteInvoicingForm };

            if (cleaned.preliminary_exchange_rate_date_reference === 'FECHA_FIJA') {
            delete cleaned.preliminary_days;
            delete cleaned.preliminary_months;
            } else {
            delete cleaned.preliminary_fixed_date;
            }

            if (cleaned.final_exchange_rate_date_reference === 'FECHA_FIJA') {
            delete cleaned.final_days;
            delete cleaned.final_months;
            } else {
            delete cleaned.final_fixed_date;
            }

            // ✅ Usa el objeto limpio
            updateQuoteInvoicing(Number(quoteForm.id), cleaned, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({
                title: 'Éxito',
                message: response.message
                });
                toast.success(response.message, {
                autoClose: 2500
                });
                setErrorFields(undefined);
                setQuoteInvoicingForm({ ...quoteInvoicingForm, ...response.data.quote_invoicing });
                setQuoteForm({
                ...quoteForm,
                ...response.data.quote,
                last_modified_user: `${response.data.quote.user.first_name} ${response.data.quote.user.last_name}`,
                });
            
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                title: 'Error',
                message: response.message
                });
                toast.error(response.message);
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
            });
        }
        };
        // Función para actualizar los datos
    if (verifyingQuote) {
        return (
            <DefaultCard>
                <LazyLoading height={300} />
            </DefaultCard>
        );
    }

    if (quoteForm.sector_business_rules_enum === SectorBusinessRulesEnum.WHS_Almacenamiento) {
        return (
            <WareHouseQuoteEditContainer
                id={parseInt(id)}
                quoteForm={quoteForm}
                setQuoteForm={setQuoteForm}
                societies={societies}
                clients={clients}
                clientContacts={clientContacts}
                paymentConditions={paymentConditions}
                references={references}
                paymentMethods={paymentMethods}
                currencies={currencies}
                offerTypes={offerTypes}
                sectors={sectors}
                errorFields={errorFields}
                condition={condition}
                disableUpdate={disableUpdate}
                loadingClient={fetchingGetClientBySociety}
                isEdit={true}
                fetchingQuote={fetchingEditQuote}
                update={update}
                sectorId={sectorId}
                fetchingQuoteInvoicingByQuote={fetchingQuoteInvoicingByQuote}
                fetchingUpdateQuoteInvoicing={fetchingUpdateQuoteInvoicing}
                updateInvoicing={updateInvoicing}
                exchangeRateDate={exchangeRateDate}
                exchangeRateDateSource={exchangeRateDateSource}
                quoteInvoicingForm={quoteInvoicingForm}
                setQuoteInvoicingForm={setQuoteInvoicingForm}
                units={units}
            />
        );
    }

    return (
        <>
            <QuoteEditContainer
                id={parseInt(id)}
                quoteForm={quoteForm}
                setQuoteForm={setQuoteForm}
                societies={societies}
                clients={clients}
                paymentConditions={paymentConditions}
                sectorBusinessRulesEnum={sectorBusinessRulesEnum}
                references={references}
                paymentMethods={paymentMethods}
                currencies={currencies}
                offerTypes={offerTypes}
                sectors={sectors}
                clientContacts={clientContacts}
                errorFields={errorFields}
                condition={condition}
                disableUpdate={disableUpdate}
                loadingClient={fetchingGetClientBySociety}
                isEdit={true}
                fetchingQuote={fetchingEditQuote}
                quoteCheckDocument={quoteCheckDocument}
                quoteRouteSegments={quoteRouteSegments}
                setQuoteRouteSegments={setQuoteRouteSegments}
                reloadQuoteRouteSegmentTable={reloadQuoteRouteSegmentTable}
                update={update}
                sectorId={sectorId}
                reloadQuote={edit}
                currencyId={currencyId}
                quoteInvoicingForm={quoteInvoicingForm}
                setQuoteInvoicingForm={setQuoteInvoicingForm}
                fetchingQuoteInvoicingByQuote={fetchingQuoteInvoicingByQuote}
                fetchingUpdateQuoteInvoicing={fetchingUpdateQuoteInvoicing}
                updateInvoicing={updateInvoicing}
                exchangeRateDate={exchangeRateDate}
                exchangeRateDateSource={exchangeRateDateSource}
                units={units}
                
            />
        </>
    );
};

export default QuoteEdit;